import type { NormalizedMarketRow } from '@/types/market';
import type { BenchmarkStats, ScoreBand } from '@/types/insights';
import { clamp, zScore } from '@/utils/numbers';
import { scoringWeights } from '@/config/scoringWeights';
import { magnitudeBand } from './bands';

/**
 * buyerCompetition: 0..100, higher = buyers are competing harder for listings
 * in this geography right now (high sale-to-list ratio, short DOM, few price
 * cuts, pending sales keeping pace with new listings).
 */
export function computeBuyerCompetition(row: NormalizedMarketRow, benchmark: BenchmarkStats): ScoreBand {
  const w = scoringWeights.buyerCompetition;

  const zStl = zScore(row.saleToListRatioPct, benchmark.meanByField.saleToListRatioPct, benchmark.stdByField.saleToListRatioPct);
  const zDom = zScore(row.medianDaysOnMarket, benchmark.meanByField.medianDaysOnMarket, benchmark.stdByField.medianDaysOnMarket);
  const zCut = zScore(
    row.priceReductionSharePct,
    benchmark.meanByField.priceReductionSharePct,
    benchmark.stdByField.priceReductionSharePct,
  );

  const pendingToNewRatio =
    row.pendingSales !== undefined && row.newListings ? row.pendingSales / row.newListings : undefined;

  const parts: number[] = [];
  if (zStl !== undefined) parts.push(zStl * w.saleToListZWeight);
  if (zDom !== undefined) parts.push(zDom * w.domZWeight);
  if (zCut !== undefined) parts.push(zCut * w.priceCutZWeight);
  if (pendingToNewRatio !== undefined) parts.push((pendingToNewRatio - 1) * w.pendingToNewZWeight);

  if (parts.length === 0) return magnitudeBand(undefined);

  const composite = parts.reduce((a, b) => a + b, 0);
  const scaled = clamp(w.center + composite, 0, 100);
  return magnitudeBand(scaled);
}
